import React, { useCallback, useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import Title from "~/shared/Title";
import TextareaInput from "~/shared/TextareaInput";
import { InputContent, Table } from "./styles";
import Button from "~/shared/Btn";
import Request from "~/services/request";
import yup from "~/utils/yup";
import { toast } from "react-toastify";
import Modal from "~/shared/ModalWithTopBar";
import { formatDateAndTime } from "~/utils/date";
import useAuth from "~/hooks/useAuth";
import requestReason from "~/services/requestReason";
import Filters from "./JobFilter";
import Jobs from "~/services/jobs";
import { getAddressFormatted } from "~/utils/address";
import SelectInput from "~/shared/SelectInput";
import Uploader from "~/components/RequestCreationModal/Uploader";
import useRequestCreatoionStats from "~/components/RequestCreationModal/useRequestCreatoionStats";

export type JobDetails = {
    jobId: number;
    jobDate: Date;
    customerCode?: string;
    address: string;
    partnerId?: number;
};

type CreationRequestProps = {
    isOpen: boolean;
    onClose: () => void;
    afterPost: () => void;
    job?: JobDetails;
    customerId: number;
    size?: string;
    isContestation?: boolean;
};

type RequestReasonProps = {
    id: number;
    reason: string;
    status: "ativo" | "inativo";
    fateReason: "fotop" | "fotografo" | "ambos";
};

const schema = yup.object().shape({
    jobId: yup.number().notRequired(),
    customerId: yup.number().required(),
    reasonIds: yup.array().required(),
    observation: yup.string().required(),
    destination: yup.string().required(),
});

const allowedMimeTypes = ["image/jpg", "image/jpeg"];


const getSelectInputReasonList = (reasonList: RequestReasonProps[], isContestation: boolean) => {
    if (isContestation) {
        return reasonList
            .map((item) => ({ label: item.reason, value: String(item.id) }))
            .filter((reason) => reason.label.toLowerCase() === "contestação");
    }

    return reasonList
        .map((item) => ({ label: item.reason, value: String(item.id) }))
        .filter((reason) => reason.label.toLowerCase() !== "contestação");
};

const RequestCreationModal = ({
    isOpen,
    job,
    onClose,
    customerId,
    afterPost,
    size = "sm",
    isContestation,
}: CreationRequestProps) => {
    const { user } = useAuth();
    const [values, setValues] = useState<Record<string, any>>();
    const [errors, setErrors] = useState<Record<string, any>>();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedReason, setSeletecReason] = useState<RequestReasonProps>();
    const [reasonRequestList, setReasonRequestList] = useState<RequestReasonProps[]>([]);
    const [jobs, setJobs] = useState([]);
    const [loadJobs, setLoadJobs] = useState(false);
    const [selectedJob, setSelectedJob] = useState<Record<string, any>>();
    const [showList, setShowList] = useState(true);
    const [showUploader, setShowUploader] = useState(false);

    const {  startUploading, setCurrentRequestId, tryAgain, onError, setFileToUpload, fileToUpload, onComplete, abortUpload } = useRequestCreatoionStats()

    const handleChange = (name: string, value: any) => {
        setValues((prev) => ({ ...prev, [name]: value }));
    };

    const getReasonRequests = useCallback(async () => {
        return requestReason
            .list()
            .then((res) => {
                setReasonRequestList(res);
            })
            .catch((e) => {
                toast.error(
                    `Não foi possível recuperar os motivos da solicitação: ${e?.message?.response?.data ?? e.message} `
                );
            });
    }, []);

    const listJobs = useCallback(async (filters = {}) => {
        setLoadJobs(true);

        const jobFilters = {
            ...filters,
            status: undefined,
        };

        return Jobs.search(1, jobFilters)
            .then((res) => {
                if (!res.data.jobs.length) {
                    toast.error(`Nenhum job encontrado`);
                }

                setJobs(res.data.jobs);
            })
            .catch((e) => {
                toast.error(
                    `Não foi possível recuperar a listagem de jobs: ${e?.message?.response?.data ?? e.message} `
                );
            })
            .finally(() => setLoadJobs(false));
    }, []);

    const createRequest = async (data) => {
        if (Object.values(fileToUpload).length > 5) {
            setIsLoading(false);
            return toast.error("Quantidade máxima de anexos exedida, limite de 5 anexos por solicitação");
        }

        try {
            const request: Record<string, any> = await Request.create(data);

            if (!!Object.values(fileToUpload)) {
                setCurrentRequestId(request.id)
                await startUploading(request.id);
            }

            toast.success(`${isContestation ? "Contestação criada com sucesso." : "Solicitação criada com sucesso."}`);

        } catch (err) {
            toast.error(`Não foi criar a solicitação: ${err?.response?.data?.msg || err.message}`);
        } finally {
            setIsLoading(false);
            afterPost();
        }
    };

    const handleCreateRequest = async () => {
        setIsLoading(true);

        if (!job && values?.jobId && values?.customerCode) {
            toast.error(`O Id do Fotop ou o Id do cliente precisa ser informado`);
            return;
        }

        let reason = selectedReason;
        if (isContestation) {
            reason = reasonRequestList.find((reason) => reason.reason.toLowerCase() === "contestação");
        }

        const requestCreationData = {
            jobId: job ? job.jobId : selectedJob?.id,
            customerId: customerId,
            organizationId: user.id_organizacoes,
            customerCode: job ? job?.customerCode : selectedJob?.customerCode,
            observation: values?.observation,
            reasonIds: [reason?.id],
            destination: reason?.fateReason,
            partnerId: job?.partnerId
                ? job?.partnerId
                : selectedJob?.photographer?.id
                ? selectedJob?.photographer?.id
                : null,
            origin: "cliente",
        };

        const validFields = await schema.validate(requestCreationData, { abortEarly: false }).catch((err) => {
            err.inner.map((erro) => {
                setErrors((prev) => ({ ...prev, [erro.path]: erro.message }));
            });
        });

        if (
            ["fotografo", "ambos"].includes(reason?.fateReason) &&
            !job?.partnerId &&
            !selectedJob?.photographer?.id
        ) {
            setIsLoading(false);
            return toast.error("Para esse motivo da solicitação e necessário ter um fotografo vinculado ao job.");
        }

        if (!validFields) {
            setIsLoading(false);
            return toast.error("Campos obirgatórios ausentes");
        }

        return createRequest(requestCreationData)
    };

    const handleFilter = async (filters: Record<string, any>) => {
        if (!Object.keys(filters).length) return;
        setShowList(true);
        setSelectedJob({});
        return listJobs(filters);
    };

    const cleanFields = () => {
        setValues({});
        setSelectedJob({});
        setShowList(true);
        setErrors({});
        setJobs([]);
    };

    useEffect(() => {
        getReasonRequests();
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onHide={() => {
                cleanFields();
                setFileToUpload({})
                setShowUploader(false)
                onClose();
            }}
            size={size}
        >
            <>
                <div style={{ margin: "1rem 0" }}>
                    <Title level="2">{isContestation ? "Realizar contestação" : "Realizar solicitação"}</Title>
                </div>
                <Row>
                    <Col>
                        <SelectInput
                            name="reasonIds"
                            label="Motivo"
                            placeholder={isContestation ? "" : "Motivo solicitação"}
                            onChange={(name, value) => {
                                handleChange(name, value);
                                const selected = reasonRequestList.find((reason) => String(reason.id) === value);
                                setSeletecReason(selected);
                            }}
                            options={getSelectInputReasonList(reasonRequestList, isContestation)}
                            value={values?.reasonIds}
                            error={errors?.reasonIds}
                            required
                        />
                    </Col>
                </Row>
                <div>{!job ? <Filters onFilter={handleFilter} loadJobs={loadJobs} /> : ""}</div>
                {jobs && jobs.length && showList ? (
                    <Row>
                        <Title level="3">Jobs encontrados</Title>
                        <div style={{ height: "200px", overflow: "auto" }}>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Id Fotop</th>
                                        <th>Id Cliente</th>
                                        <th>Endereço</th>
                                        <th>Data</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobs.map((job) => (
                                        <tr
                                            key={job.id}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => {
                                                setSelectedJob(job);
                                                setShowList(false);
                                            }}
                                        >
                                            <td>{job.id}</td>
                                            <td>{job.customerCode}</td>
                                            <td>
                                                {getAddressFormatted({
                                                    addressParts: {
                                                        address: job?.address?.street,
                                                        number: job?.address?.number,
                                                        cityName: job?.address?.city.name,
                                                        neighborhood: job?.address?.neighborhood,
                                                        complement: job?.address?.complement,
                                                    },
                                                })}
                                            </td>
                                            <td>{job?.scheduleDate ? formatDateAndTime(job?.scheduleDate) : ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div>
                                {errors?.jobId ? (
                                    <small className="error" style={{ color: "#a7258b" }}>
                                        * {errors?.jobId}
                                    </small>
                                ) : (
                                    ""
                                )}
                            </div>
                        </div>
                    </Row>
                ) : (
                    ""
                )}

                <div>
                    {job ? (
                        <div>
                            <Row>
                                <Title level="2">Informações do job</Title>
                            </Row>
                            <Row>
                                <Col>
                                    <strong>ID do fotop</strong>
                                    <p>{job?.jobId}</p>
                                </Col>
                                <Col>
                                    <strong>ID do cliente</strong>
                                    <p>{job?.customerCode}</p>
                                </Col>
                            </Row>
                            <Row className="my-4">
                                <Col>
                                    <strong>Data e Horário do Job</strong>
                                    <p>{job?.jobDate ? formatDateAndTime(job?.jobDate) : ""}</p>
                                </Col>
                                <Col>
                                    <strong>Endereço</strong>
                                    <p>{job?.address}</p>
                                </Col>
                            </Row>
                        </div>
                    ) : (
                        ""
                    )}

                    {selectedJob && !showList ? (
                        <div>
                            <Row>
                                <Title level="2">Informações do job</Title>
                            </Row>
                            <Row>
                                <Col>
                                    <strong>ID do fotop</strong>
                                    <p>{selectedJob?.id}</p>
                                </Col>
                                <Col>
                                    <strong>ID do cliente</strong>
                                    <p>{selectedJob?.customerCode}</p>
                                </Col>
                            </Row>
                            <Row className="my-4">
                                <Col>
                                    <strong>Data e Horário do Job</strong>
                                    <p>
                                        {selectedJob?.scheduleDate ? formatDateAndTime(selectedJob?.scheduleDate) : ""}
                                    </p>
                                </Col>
                                <Col>
                                    <strong>Endereço</strong>
                                    <p>
                                        {getAddressFormatted({
                                            addressParts: {
                                                address: selectedJob?.address?.street,
                                                number: selectedJob?.address?.number,
                                                cityName: selectedJob?.address?.city.name,
                                                neighborhood: selectedJob?.address?.neighborhood,
                                                complement: selectedJob?.address?.complement,
                                            },
                                        })}
                                    </p>
                                </Col>
                            </Row>
                        </div>
                    ) : (
                        ""
                    )}

                    <InputContent>
                        <div className="bar"></div>
                        <TextareaInput
                            name="observation"
                            label="Descrição"
                            value={values?.observation}
                            onChange={handleChange}
                            error={errors?.observation}
                            rows={6}
                            required
                        />
                    </InputContent>
                    {showUploader ? (
                        <InputContent>
                            <div className="bar"></div>
                            <Uploader
                                multiple
                                tryAgain={tryAgain}
                                abortUpload={abortUpload}
                                fileToUpload={fileToUpload}
                                setFileToUpload={setFileToUpload}
                                onComplete={onComplete}
                                allowedMimeTypes={allowedMimeTypes}
                            />
                            <div className="bar"></div>
                        </InputContent>
                    ) : (
                        ""
                    )}

                    <Row style={{ textAlign: "right" }}>
                        <Col sm={6}>
                            {showUploader ? (
                                ""
                            ) : (
                                <Button block onClick={() => setShowUploader(true)}>
                                    Enviar anexo
                                </Button>
                            )}
                        </Col>
                        <Col sm={6}>
                            <Button block loading={isLoading} onClick={handleCreateRequest}>
                                {isContestation ? "Enviar contestação" : "Enviar solicitação"}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </>
        </Modal>
    );
};

export default RequestCreationModal;
